/*
 * Copyright 2026 Bytefinger Technology (HK) Co., Limited (https://bytefinger.ai)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

export interface PublicConfiguration {
  agent_store_allow_both_ui?: boolean;
  agent_store_frontend_version?: 'v1' | 'v2';
  default_locale: 'en-US' | 'zh-CN' | 'zh-HK';
}

/** Only consume the unauthenticated, allowlisted configuration contract. */
export async function fetchPublicConfiguration(): Promise<PublicConfiguration> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 5000);
  try {
    const response = await fetch('/api/system/public_config', {
      credentials: 'omit',
      signal: controller.signal,
    });
    if (response.ok) {
      const data = await response.json();
      const locale = data?.default_locale;
      if (data?.code === 0 && ['en-US', 'zh-CN', 'zh-HK'].includes(locale)) {
        return {
          default_locale: locale,
          ...(typeof data.agent_store_allow_both_ui === 'boolean'
            ? { agent_store_allow_both_ui: data.agent_store_allow_both_ui }
            : {}),
          ...(data.agent_store_frontend_version
            ? {
                agent_store_frontend_version:
                  data.agent_store_frontend_version === 'v1'
                    ? ('v1' as const)
                    : ('v2' as const),
              }
            : {}),
        };
      }
    }
  } catch (error) {
    // Unavailable/malformed configuration must not prevent application boot.
    void error;
  } finally {
    clearTimeout(timeout);
  }
  return { default_locale: 'en-US' };
}
